// /admin/production — the Kitchen Manager's screen (kitchen flow, lane C;
// Batch 4 layout). Three tabs — Today (what needs the kitchen now and the
// week ahead), Events (production plans) and Requests (my requests, check
// in what arrived) — and the tools in the header: Ask for stock, Make food,
// and under More: Send back to store, Count the kitchen, Throw away, Basics.
// Kitchen actions are queued on this tablet when the connection drops and
// sent by themselves when it returns.
import { RecipeCardHost } from '@/components/recipe-card/RecipeCardHost'
import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { format } from 'date-fns'
import { ArrowLeft, ChevronDown, CloudOff } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import { getStoredUserRole } from '@/lib/staff-roles'
import { T } from '@/lib/inventoryFlow/strings'
import { useKitchenActions } from '@/lib/inventoryFlow/useKitchenActions'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'
import { pollInterval, useRequisitionStream } from '@/lib/inventoryFlow/requisitionStream'
import type { Requisition, RequisitionKind } from '@/lib/inventoryFlow/types'
import { initialView, isTabView, type ProductionView } from '@/lib/inventoryFlow/todayLogic'
import { TT } from '@/lib/inventoryFlow/todayStrings'
import { useKitchenFlowGate } from '@/hooks/useKitchenFlowEnabled'
import { cn } from '@/lib/utils'
import { RequestBuilder } from './RequestBuilder'
import { LiveDot, RequisitionList } from './RequisitionList'
import { RequisitionPanel, type KitchenRun } from './RequisitionPanel'
import { MakeFoodTab } from './MakeFoodTab'
import { PlansTab } from './PlansTab'
import { WasteTab } from './WasteTab'
import { BasicsTab } from './BasicsTab'
import { CountSheet } from './CountSheet'
import { TODAY_KEY, TodayTab } from './TodayTab'
import { QueueProblems } from './QueueProblems'

export function ProductionPage() {
  const { enabled, isLoading, isError, retry } = useKitchenFlowGate()
  if (isLoading) return <div className="p-6 text-muted-foreground">{T.loading}</div>
  if (isError) {
    // Could not read settings (e.g. no connection) — never claim "off".
    return (
      <div className="space-y-3 p-6">
        <p className="text-muted-foreground">{T.settingsUnreachable}</p>
        <Button onClick={retry}>{T.retryNow}</Button>
      </div>
    )
  }
  if (!enabled) return <div className="p-6 text-muted-foreground">{T.notEnabled}</div>
  // The host gives every tab below one recipe card to open; it renders nothing
  // itself unless the tenant has switched recipes on.
  return (
    <RecipeCardHost>
      <ProductionScreen />
    </RecipeCardHost>
  )
}

/** The header's tools and their titles. */
const TOOL_TITLE: Record<Exclude<ProductionView, 'today' | 'events' | 'requests'>, string> = {
  ask: TT.askForStock,
  make: TT.makeFood,
  return: TT.sendBack,
  count: TT.count,
  waste: TT.throwAway,
  basics: TT.basics,
}

/** Which plan the Events tab opens, and how. `n` remounts it on every open. */
type PlanOpen = { id: string; wrapUp: boolean; fromToday: boolean; n: number } | null

function ProductionScreen() {
  const role = getStoredUserRole()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useToast()
  const notify = useCallback(
    (kind: 'success' | 'error', message: string) => toast({ title: message, variant: kind === 'error' ? 'destructive' : undefined }),
    [toast],
  )
  const { run, pending, flush, flushNow, retry, discard, waiting: mineWaiting, failed, held } = useKitchenActions(
    useCallback((label: string, message: string) => notify('error', `${label}: ${message}`), [notify]),
    useCallback((label: string) => notify('error', T.queueGaveUp(label)), [notify]),
  )
  // ?plan=<id> (from a catering booking) opens that plan; ?tab= a tab or tool.
  const search = useSearch({ strict: false }) as { plan?: string; tab?: string }
  const [view, setViewState] = useState<ProductionView>(() => initialView(search, role))
  // The tab a tool returns to.
  const [lastTab, setLastTab] = useState<ProductionView>(() => {
    const v = initialView(search, role)
    return isTabView(v) ? v : 'today'
  })
  const [planOpen, setPlanOpen] = useState<PlanOpen>(() => (search.plan ? { id: search.plan, wrapUp: false, fromToday: false, n: 0 } : null))
  const [selected, setSelected] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const setView = useCallback((v: ProductionView, plan?: string) => {
    setViewState(v)
    if (isTabView(v)) setLastTab(v)
    // Keep the address in step so a refresh (or a shared link) lands here.
    void navigate({ to: '/admin/production', search: plan ? { tab: v, plan } : { tab: v }, replace: true })
  }, [navigate])

  const openPlan = useCallback((id: string, opts?: { wrapUp?: boolean }) => {
    setPlanOpen((p) => ({ id, wrapUp: !!opts?.wrapUp, fromToday: true, n: (p?.n ?? 0) + 1 }))
    setView('events', id)
  }, [setView])
  const openEvents = useCallback(() => {
    // "All events" / the Events tab: the list, not the last plan opened.
    setPlanOpen((p) => (p ? { id: '', wrapUp: false, fromToday: false, n: p.n + 1 } : null))
    setView('events')
  }, [setView])
  const openRequest = useCallback((id: string) => {
    setSelected(id)
    setView('requests')
  }, [setView])

  const refetch = useCallback(() => void qc.invalidateQueries({ queryKey: ['inventory-flow'] }), [qc])
  const live = useRequisitionStream({ enabled: true, onEvent: refetch, onReconnect: () => { refetch(); void flush() } })

  const { data: listRes, isLoading: listLoading, isError: listError, refetch: listRefetch, isFetching: listFetching } = useQuery({
    queryKey: ['inventory-flow', 'requisitions', 'all'],
    // Every open request however old, plus the newest 100.
    queryFn: () => apiClient.getRequisitions({ limit: 100, include_open: true }),
    refetchInterval: pollInterval(live),
  })
  const list: Requisition[] = listRes?.data ?? []
  // The subtitle's day is the business day Today counts from (after midnight
  // the kitchen is still on yesterday's service); read from the cache only.
  const { data: todayRes } = useQuery({ queryKey: TODAY_KEY, queryFn: () => apiClient.getKitchenToday(), enabled: false })
  const businessDay = todayRes?.data?.today ? new Date(`${todayRes.data.today}T12:00:00`) : new Date()
  // The badge comes from the server's count, not from a page of the list.
  const { data: summaryRes } = useQuery({
    queryKey: ['inventory-flow', 'summary'],
    queryFn: () => apiClient.getRequisitionSummary(),
    refetchInterval: pollInterval(live),
  })
  const toReceive = summaryRes?.data?.awaiting_kitchen_receipt ?? list.filter((r) => r.kind === 'issue' && r.awaiting_receipt).length

  const send = (kind: RequisitionKind) => async (body: { lines: { stock_item_id: string; qty: number }[]; needed_by?: string; notes?: string }) => {
    setBusy(true)
    try {
      const res = await run<Requisition>(kind === 'return' ? T.sendReturn : T.sendRequest, 'POST', '/inventory-flow/requisitions', { kind, submit: true, ...body })
      notify('success', res.status === 'queued' ? T.queued : kind === 'return' ? T.returnSentOk : T.sentOk)
      if (res.status === 'sent') openRequest(res.data.id)
      return true
    } catch (e) {
      notify('error', friendlyError(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  const tabs: { id: ProductionView; label: string; badge?: number }[] = [
    { id: 'today', label: TT.tabToday },
    { id: 'events', label: TT.tabEvents },
    { id: 'requests', label: TT.tabRequests, badge: toReceive },
  ]

  return (
    <div className="space-y-5 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold tracking-tight">{T.productionTitle}</h1>
          <p className="text-muted-foreground">{TT.subtitle(format(businessDay, 'EEEE d MMMM'))}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LiveDot status={live} />
          {mineWaiting.length > 0 && (
            <Button variant="outline" className="border-amber-400 text-amber-800 dark:text-amber-200" onClick={() => void flushNow()}>
              <CloudOff className="mr-2 h-4 w-4" /> {T.notSynced(mineWaiting.length)} · {T.retryNow}
            </Button>
          )}
          <Button variant={view === 'ask' ? 'default' : 'outline'} onClick={() => setView('ask')}>{TT.askForStock}</Button>
          <Button variant={view === 'make' ? 'default' : 'outline'} onClick={() => setView('make')}>{TT.makeFood}</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={view === 'return' || view === 'count' || view === 'waste' || view === 'basics' ? 'default' : 'outline'}>
                {TT.more} <ChevronDown className="ml-1 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setView('return')}>{TT.sendBack}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setView('count')}>{TT.count}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setView('waste')}>{TT.throwAway}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setView('basics')}>{TT.basics}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <QueueProblems failed={failed} held={held} onRetry={(id) => void retry(id)}
        onDiscard={(id) => { discard(id); notify('success', T.queueDiscarded) }} />

      <div className="flex w-fit max-w-full flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={view === t.id} onClick={() => (t.id === 'events' ? openEvents() : setView(t.id))}
            className={cn('flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors',
              view === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
            {t.label}
            {!!t.badge && <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs text-white">{t.badge}</span>}
          </button>
        ))}
      </div>

      {!isTabView(view) && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" className="px-2" onClick={() => setView(lastTab)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> {TT.back}
          </Button>
          <h2 className="text-lg font-semibold">{TOOL_TITLE[view as keyof typeof TOOL_TITLE]}</h2>
        </div>
      )}

      {view === 'today' && (
        <TodayTab notify={notify} run={run as KitchenRun} queue={pending} live={live} onOpenPlan={openPlan} onOpenRequest={openRequest} onAllEvents={openEvents} />
      )}
      {view === 'events' && (
        <PlansTab key={planOpen?.n ?? 0} notify={notify} initialPlanId={planOpen?.id || undefined} initialWrapUp={planOpen?.wrapUp}
          onExit={planOpen?.fromToday ? { label: TT.backToday, go: () => setView('today') } : undefined} onOpenRequest={openRequest} />
      )}
      {view === 'ask' && <RequestBuilder kind="issue" busy={busy} onSend={send('issue')} />}
      {view === 'return' && <RequestBuilder kind="return" busy={busy} onSend={send('return')} />}
      {view === 'make' && <MakeFoodTab run={run as KitchenRun} notify={notify} />}
      {view === 'count' && <CountSheet mode="kitchen" notify={notify} />}
      {view === 'waste' && <WasteTab notify={notify} />}
      {view === 'basics' && <BasicsTab notify={notify} side="kitchen" run={run as KitchenRun} queue={pending} />}
      {view === 'requests' && (
        <div className="grid gap-4 lg:grid-cols-[minmax(18rem,26rem)_1fr]">
          {listLoading ? (
            <p className="p-4 text-muted-foreground">{TT.loading}</p>
          ) : listError ? (
            <div className="space-y-3 rounded-lg border p-4">
              <p className="text-muted-foreground">{TT.loadFailed}</p>
              <Button variant="outline" disabled={listFetching} onClick={() => void listRefetch()}>{TT.retry}</Button>
            </div>
          ) : (
            <RequisitionList items={list} selectedId={selected} onSelect={setSelected} />
          )}
          <div className="rounded-lg border bg-card p-4">
            {selected ? (
              <RequisitionPanel key={selected} id={selected} role={role} kitchenRun={run as KitchenRun} kitchenQueue={pending} notify={notify} />
            ) : (
              <p className="text-muted-foreground">{T.empty}</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
